import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Button } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { FileInputProps } from './FileInput.types'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { LayoutProps, SpaceProps } from 'styled-system'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { Upload } from '@dtx-company/shared-components/src'
import { getExtensionFromFileName, isValidFileExtension } from '@app/common/src/utils/utils'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useRef } from 'react'
import Image from 'next/image'
import styled from 'styled-components'

const StyledFileInput = styled.input`
  display: none;
`

export const FileInput = ({
  fileName,
  handleChange,
  label = 'Attach a File',
  labelStyle,
  placeholder = 'yourfile.pdf',
  inputRef,
  accept,
  inputName = 'file',
  buttonId,
  maxSize,
  showMaxSize = true,
  maxSizeProps = { color: 'primary.grey' },
  border,
  borderColor,
  labelWidth = '100%',
  clearImage,
  isEdit = false,
  loading = false,
  loader,
  inputProps = {},
  textProps = {},
  buttonVariant = 'primary',
  customStartAdornment,
  startAdornmentWrapperProps = {},
  handleEndArdornmentClick,
  showUploadIconButton = false,
  uploadIconBtnProps,
  sharedInputProps,
  ...rest
}: FileInputProps & SpaceProps & LayoutProps): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const labelMarginBottom = isEdit ? '8px' : '11px'
  function getButtonLabel(): string {
    if (loading) return ''
    else return !fileName ? 'Upload' : 'Change'
  }
  return (
    // TODO: should this be custom made? - DF
    // TODO: make file upload component a dropzone and add frontend size restriction based on maxSize prop - KH
    <Box
      display="block"
      data-testid="destination-form-file"
      {...(maxSize && showMaxSize && { position: 'relative' })}
      onClick={() => fileInputRef?.current?.click()}
      id={buttonId}
      {...rest}
    >
      {maxSize && showMaxSize && (
        <Box position="absolute" top={0} right={0}>
          <Text variant="body/small" {...maxSizeProps}>
            Max Size: {maxSize}MB
          </Text>
        </Box>
      )}
      <Input
        controlled
        label={label}
        type="text"
        border={border ?? (borderColor ? `2px solid ${borderColor}` : null)}
        disabled
        backgroundColor="primary.white"
        placeholder={placeholder}
        onChange={() => ({})}
        labelProps={{ marginBottom: labelMarginBottom, width: labelWidth, ...labelStyle }}
        inputProps={{
          ref: inputRef,
          ...sharedInputProps
        }}
        {...inputProps}
        value={fileName || ''}
        maxWidth={inputProps.maxWidth ?? 'initial'}
        startAdornment={
          customStartAdornment || (
            <Box m="0 14px">
              <Image
                width={20}
                height={20}
                src="/images/landing-page-destinations/paperclip.svg"
                alt=""
              />
            </Box>
          )
        }
        startAdornmentWrapperProps={startAdornmentWrapperProps}
        endAdornment={
          <>
            <StyledFileInput
              name={inputName}
              aria-label={placeholder}
              ref={fileInputRef}
              data-testid="StyledFileInput"
              type="file"
              onChange={e => {
                const file = e.target.files?.[0]
                if (file && !isValidFileExtension(file)) {
                  const extension = getExtensionFromFileName(file.name)
                  sendErrorNotification(`The ${extension} file type is not supported.`)
                  return
                }

                handleChange(e)
              }}
              {...(accept && { accept })}
            />
            {clearImage && (
              <Box display={fileName ? 'flex' : 'none'} backgroundColor="white" paddingLeft="8px">
                <Button
                  position="relative"
                  minWidth="69px"
                  colorVariant="secondary"
                  type="button"
                  height="30px"
                  marginRight="7px"
                  padding="0"
                  data-testid={`clear-${fileName}`}
                  onClick={e => {
                    e.stopPropagation()
                    clearImage()
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                >
                  <Text color="inherit" {...textProps}>
                    Clear
                  </Text>
                </Button>
              </Box>
            )}
            <Button
              minWidth="69px"
              colorVariant={buttonVariant}
              type="button"
              onClick={() => handleEndArdornmentClick?.()}
              height="30px"
              marginRight="7px"
              padding="0"
              id={buttonId}
              disabled={loading}
              {...uploadIconBtnProps}
              {...textProps}
            >
              {showUploadIconButton ? (
                <Upload aria-label="upload-icon" />
              ) : (
                <Text color="inherit" fontWeight="bold" {...textProps}>
                  {getButtonLabel()}
                </Text>
              )}
              {loading && loader && loader}
            </Button>
          </>
        }
      />
    </Box>
  )
}
